import { useState, useEffect } from 'react';
import {
  Plus, Link2, ExternalLink, Copy, Check, Search, Filter,
  Globe, FileText, Video, ClipboardList, Calculator, BarChart2,
  Trash2, Edit2, ChevronDown, ChevronUp, Zap, Eye, Loader2
} from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import apiClient from '@/services/api/apiClient';
import magnetService from '@/services/api/magnetService';
import { Pagination } from '@/components/common/Pagination';
import { PageHeader } from '@/components/common/PageHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

const CONTENT_TYPES = [
  { value: 'article',     label: 'Article / Blog',     icon: FileText,    desc: 'Long-form educational content page' },
  { value: 'pdf_page',    label: 'PDF Page',           icon: FileText,    desc: 'Page that hosts a downloadable PDF' },
  { value: 'video_page',  label: 'Video Page',         icon: Video,       desc: 'Video with supporting content' },
  { value: 'assessment',  label: 'Maturity Assessment',icon: BarChart2,   desc: 'Contact self-selects their maturity band' },
  { value: 'evaluation',  label: 'Readiness Evaluation',icon: ClipboardList, desc: 'Multi-question evaluation form' },
  { value: 'calculator',  label: 'ROI Calculator',     icon: Calculator,  desc: 'Interactive calculator tool' },
  { value: 'checklist',   label: 'Checklist / Tool',   icon: ClipboardList, desc: 'Downloadable checklist or tool' },
] as const;

const JOURNEY_STAGES = [
  { id: 1,  label: 'Account Context',      phase: 'Discovery'  },
  { id: 2,  label: 'Maturity Mirror',       phase: 'Discovery'  },
  { id: 3,  label: 'Pain Framing',          phase: 'Discovery'  },
  { id: 4,  label: 'Maturity Gap',          phase: 'Discovery'  },
  { id: 5,  label: 'MES Reality',           phase: 'Discovery'  },
  { id: 6,  label: 'Alignment Call #1',     phase: 'Advisory'   },
  { id: 7,  label: 'Architecture Thinking', phase: 'Advisory'   },
  { id: 8,  label: 'Multi-System Coexist',  phase: 'Advisory'   },
  { id: 9,  label: 'Data Readiness',        phase: 'Education'  },
  { id: 10, label: 'Automation Timing',     phase: 'Education'  },
  { id: 11, label: 'Decision Intelligence', phase: 'Education'  },
  { id: 12, label: 'Confirmation Call #2',  phase: 'Validation' },
  { id: 13, label: 'Future-State Snapshot', phase: 'Validation' },
  { id: 14, label: 'MQL Handoff',           phase: 'Validation' },
];

const PHASE_COLORS: Record<string, string> = {
  Discovery:  'bg-blue-100   text-blue-700   dark:bg-blue-950/40   dark:text-blue-400',
  Advisory:   'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400',
  Education:  'bg-amber-100  text-amber-700  dark:bg-amber-950/40  dark:text-amber-400',
  Validation: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
};

interface Magnet {
  id: string;
  title: string;
  description: string;
  type: string;
  format: string;
  author: string;
  journeySteps: number[];
  downloads: number;
  deliveries: number;
  createdDate: string;
  // Subsite URL fields
  subsiteUrl?: string;
  subsitePageSlug?: string;
  subsiteDomain?: string;
  contentType?: string;
  assetUrl?: string;
  videoLink?: string;
  assessmentConfig?: any;
  trackEvents?: string[];
}

// ── Tracked link modal ────────────────────────────────────────────────────────
function TrackedLinkModal({ magnet, onClose }: { magnet: Magnet; onClose: () => void }) {
  const [contactId, setContactId] = useState('');
  const [links, setLinks]         = useState<any>(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [contacts, setContacts]   = useState<any[]>([]);

  useEffect(() => {
    apiClient.get('/api/contacts?limit=200').then(r => setContacts(r.data.data || [])).catch(() => {});
  }, []);

  const generate = async () => {
    if (!contactId) { setError('Select a contact'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await apiClient.post(`/api/magnets/${magnet.id}/tracked-links`, { contactId });
      setLinks(data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />Generate Tracked Links
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{magnet.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors text-lg">×</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Contact</label>
            <select className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={contactId} onChange={e => setContactId(e.target.value)}>
              <option value="">Select contact…</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.email || 'no email'}</option>
              ))}
            </select>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>

          <button onClick={generate} disabled={loading || !contactId}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 transition-colors">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Generating…</> : <><Zap className="h-4 w-4" />Generate Tracked Links</>}
          </button>

          {links && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Generated links — click to copy</p>

              {[
                { label: '⭐ Personalized portal link (send this)', desc: 'Tokenized per-contact link to the WordPress portal - tracks open, download, like and form submit against this contact', value: links.portalUrl },
                { label: '📧 Email open tracking pixel',    desc: 'Embed in email HTML — fires when email is opened', value: links.emailSnippets?.trackingPixel, mono: true },
                { label: '🖱️ Click-tracked page link',      desc: 'Share this URL in email/LinkedIn — tracks when contact clicks', value: links.clickUrl },
                { label: '⬇️ Download-tracked asset link',  desc: 'Share to track PDF/file downloads', value: links.downloadUrl },
                { label: '👁️ Page-view pixel (for subsite)',desc: 'Embed in the subsite page — fires when contact visits', value: links.pageViewPixelUrl, mono: true },
                { label: '📊 Assessment URL',               desc: 'Opens assessment form with postback configured', value: links.assessUrl },
                { label: '📋 Evaluation URL',               desc: 'Opens evaluation form with postback configured', value: links.evaluateUrl },
                { label: '🎯 CTA email button',             desc: 'Ready-to-paste HTML button for emails', value: links.emailSnippets?.ctaButton, mono: true },
              ].filter(l => l.value).map((l, i) => (
                <LinkRow key={i} {...l} />
              ))}

              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-400">
                <p className="font-semibold mb-1">Subsite integration</p>
                <p>Embed the page-view pixel URL on your subsite page to auto-track visits. For assessments and evaluations, the URL includes a <code>?postback=</code> param your subsite should POST to when the form is submitted.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LinkRow({ label, desc, value, mono = false }: { label: string; desc: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="rounded-lg border border-border bg-muted/30 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
        <div>
          <p className="text-xs font-semibold text-foreground">{label}</p>
          <p className="text-[10px] text-muted-foreground">{desc}</p>
        </div>
        <button onClick={copy} className="flex items-center gap-1 px-2 py-1 rounded bg-background border border-border text-xs text-muted-foreground hover:text-foreground transition-colors">
          {copied ? <><Check className="h-3 w-3 text-emerald-500" />Copied</> : <><Copy className="h-3 w-3" />Copy</>}
        </button>
      </div>
      <div className="px-3 py-2">
        <p className={`text-[10px] break-all text-muted-foreground ${mono ? 'font-mono' : ''}`}>{value}</p>
      </div>
    </div>
  );
}

// ── Add / Edit magnet form ─────────────────────────────────────────────────────
function MagnetForm({ initial, onSave, onCancel }: {
  initial?: Partial<Magnet>;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    title: initial?.title || '',
    description: initial?.description || '',
    author: initial?.author || '',
    contentType: initial?.contentType || 'article',
    subsiteUrl: initial?.subsiteUrl || '',
    subsitePageSlug: initial?.subsitePageSlug || '',
    subsiteDomain: initial?.subsiteDomain || 'resources.cantier.com',
    assetUrl: initial?.assetUrl || '',
    videoLink: initial?.videoLink || '',
    journeySteps: initial?.journeySteps || [] as number[],
    trackEvents: initial?.trackEvents || ['open','click','download'] as string[],
  });
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState<Record<string, string>>({});

  const S = {
    input: 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors',
    label: 'block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5',
    err:   'text-xs text-red-500 mt-1',
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim())      e.title = 'Title is required';
    if (!form.author.trim())     e.author = 'Author is required';
    if (!form.subsiteUrl.trim()) e.subsiteUrl = 'Subsite page URL is required';
    if (!form.journeySteps.length) e.steps = 'Select at least one ABM stage';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({ ...form, stage: 'STAGE_1', type: 'GUIDE', format: 'PDF' });
    } finally { setSaving(false); }
  };

  const toggleStep = (step: number) => {
    setForm(p => ({
      ...p,
      journeySteps: p.journeySteps.includes(step)
        ? p.journeySteps.filter(s => s !== step)
        : [...p.journeySteps, step].sort((a, b) => a - b),
    }));
  };

  const selectedContentType = CONTENT_TYPES.find(c => c.value === form.contentType);
  const ContentIcon = selectedContentType?.icon || FileText;

  return (
    <div className="space-y-5">
      {/* Content type selector */}
      <div>
        <label className={S.label}>Content Type</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CONTENT_TYPES.map(ct => {
            const Icon = ct.icon;
            return (
              <button key={ct.value} type="button" onClick={() => setForm(p => ({ ...p, contentType: ct.value }))}
                className={`flex items-start gap-2 p-3 rounded-xl border text-left transition-all ${
                  form.contentType === ct.value
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/30 hover:bg-muted/30'
                }`}>
                <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${form.contentType === ct.value ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-xs font-semibold ${form.contentType === ct.value ? 'text-primary' : 'text-foreground'}`}>{ct.label}</p>
                  <p className="text-[10px] text-muted-foreground">{ct.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Basic fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className={S.label}>Title *</label>
          <input className={S.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="e.g. MES Maturity Self-Assessment" />
          {errors.title && <p className={S.err}>{errors.title}</p>}
        </div>
        <div className="col-span-2">
          <label className={S.label}>Description</label>
          <textarea className={`${S.input} resize-none`} rows={2} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Brief description of what the contact gets from this content" />
        </div>
        <div>
          <label className={S.label}>Author *</label>
          <input className={S.input} value={form.author} onChange={e => setForm(p => ({ ...p, author: e.target.value }))} placeholder="e.g. Cantier Editorial Team" />
          {errors.author && <p className={S.err}>{errors.author}</p>}
        </div>
        <div>
          <label className={S.label}>Subsite Domain</label>
          <input className={S.input} value={form.subsiteDomain}
            onChange={e => setForm(p => ({ ...p, subsiteDomain: e.target.value }))}
            placeholder="resources.cantier.com" />
        </div>
      </div>

      {/* URLs */}
      <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-4">
        <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5 text-primary" />Subsite URLs
        </p>
        <div>
          <label className={S.label}>
            {form.contentType === 'assessment' ? 'Assessment page URL *' :
             form.contentType === 'evaluation' ? 'Evaluation page URL *' :
             form.contentType === 'video_page' ? 'Video page URL *' :
             'Content page URL *'}
          </label>
          <input className={S.input} value={form.subsiteUrl}
            onChange={e => setForm(p => ({ ...p, subsiteUrl: e.target.value }))}
            placeholder={`https://${form.subsiteDomain || 'resources.cantier.com'}/mes-maturity-assessment`} />
          {errors.subsiteUrl && <p className={S.err}>{errors.subsiteUrl}</p>}
          <p className="text-[10px] text-muted-foreground mt-1">The page on your subsite that hosts this content. Tracked links will redirect here.</p>
        </div>
        {['pdf_page','checklist'].includes(form.contentType) && (
          <div>
            <label className={S.label}>Direct asset URL (PDF/file)</label>
            <input className={S.input} value={form.assetUrl}
              onChange={e => setForm(p => ({ ...p, assetUrl: e.target.value }))}
              placeholder="https://cdn.cantier.com/resources/mes-guide.pdf" />
            <p className="text-[10px] text-muted-foreground mt-1">Direct link to the PDF or file on your CDN. Used for download tracking.</p>
          </div>
        )}
        {['video_page','article'].includes(form.contentType) && (
          <div>
            <label className={S.label}>Video link (YouTube / Vimeo)</label>
            <input className={S.input} value={form.videoLink}
              onChange={e => setForm(p => ({ ...p, videoLink: e.target.value }))}
              placeholder="https://youtube.com/watch?v=..." />
          </div>
        )}
        <div>
          <label className={S.label}>Page slug (optional)</label>
          <input className={S.input} value={form.subsitePageSlug}
            onChange={e => setForm(p => ({ ...p, subsitePageSlug: e.target.value }))}
            placeholder="/mes-maturity-assessment" />
          <p className="text-[10px] text-muted-foreground mt-1">Relative path on the subsite. Auto-derived from the URL if left blank.</p>
        </div>
      </div>

      {/* ABM Stage mapping */}
      <div>
        <label className={S.label}>ABM Journey Stages *</label>
        <p className="text-[10px] text-muted-foreground mb-2">Which stages should this content be surfaced for?</p>
        {errors.steps && <p className={S.err + ' mb-2'}>{errors.steps}</p>}
        <div className="grid grid-cols-2 gap-1.5">
          {(['Discovery','Advisory','Education','Validation'] as const).map(phase => (
            <div key={phase} className="space-y-1">
              <p className={`px-2 py-0.5 rounded text-[10px] font-bold w-fit ${PHASE_COLORS[phase]}`}>{phase}</p>
              {JOURNEY_STAGES.filter(s => s.phase === phase).map(s => (
                <button key={s.id} type="button" onClick={() => toggleStep(s.id)}
                  className={`flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-left text-xs transition-all ${
                    form.journeySteps.includes(s.id)
                      ? 'bg-primary/10 border border-primary/30 text-primary font-medium'
                      : 'border border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                  }`}>
                  <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-[9px] font-bold shrink-0"
                    style={{ borderColor: form.journeySteps.includes(s.id) ? 'currentColor' : undefined }}>
                    {s.id}
                  </span>
                  {s.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t border-border">
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 transition-colors">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {initial?.id ? 'Save Changes' : 'Register Magnet'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:bg-muted transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Magnet card ───────────────────────────────────────────────────────────────
function MagnetCard({ magnet, onEdit, onDelete, onTrack }: {
  magnet: Magnet; onEdit: () => void; onDelete: () => void; onTrack: () => void;
}) {
  const ct = CONTENT_TYPES.find(c => c.value === magnet.contentType) || CONTENT_TYPES[0];
  const Icon = ct.icon;

  return (
    <div className="rounded-xl border border-border bg-card hover:border-primary/20 hover:shadow-sm transition-all group">
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="h-4.5 w-4.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground truncate">{magnet.title}</p>
            <p className="text-xs text-muted-foreground">{ct.label} · {magnet.author}</p>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 text-muted-foreground hover:text-red-500 transition-colors">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Description */}
        {magnet.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{magnet.description}</p>
        )}

        {/* Subsite URL */}
        {magnet.subsiteUrl && (
          <div className="flex items-center gap-1.5 text-xs">
            <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
            <a href={magnet.subsiteUrl} target="_blank" rel="noopener noreferrer"
              className="text-primary hover:underline truncate flex items-center gap-1">
              {magnet.subsiteUrl.replace('https://', '').slice(0, 50)}
              <ExternalLink className="h-2.5 w-2.5 shrink-0" />
            </a>
          </div>
        )}

        {/* Journey stages */}
        {magnet.journeySteps?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {magnet.journeySteps.slice(0, 6).map(s => {
              const stage = JOURNEY_STAGES.find(js => js.id === parseInt(String(s)));
              return (
                <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PHASE_COLORS[stage?.phase || 'Discovery']}`}>
                  {s}. {stage?.label.slice(0, 14)}
                </span>
              );
            })}
            {magnet.journeySteps.length > 6 && (
              <span className="text-[10px] text-muted-foreground">+{magnet.journeySteps.length - 6} more</span>
            )}
          </div>
        )}

        {/* Stats + CTA */}
        <div className="flex items-center justify-between pt-1 border-t border-border">
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span>{magnet.deliveries || 0} sent</span>
            <span>{magnet.downloads || 0} engaged</span>
          </div>
          <button onClick={onTrack}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 text-xs font-semibold transition-colors">
            <Link2 className="h-3 w-3" />Get Tracked Links
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: 'Article / Blog',
  pdf_page: 'PDF Document',
  video_page: 'Video',
  assessment: 'Assessment',
  evaluation: 'Evaluation',
  calculator: 'ROI Calculator',
  checklist: 'Checklist',
};

const MAGNETS_PER_PAGE = 12;

export default function MagnetsPage() {
  const { addToast } = useToast();
  const [magnets, setMagnets]         = useState<Magnet[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('all');
  const [filterStage, setFilterStage] = useState<number | null>(null);
  const [showForm, setShowForm]       = useState(false);
  const [editingMagnet, setEditingMagnet]   = useState<Magnet | null>(null);
  const [trackingMagnet, setTrackingMagnet] = useState<Magnet | null>(null);
  const [subsiteDomain, setSubsiteDomain]   = useState('resources.cantier.com');
  const [page, setPage]                     = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const r = await magnetService.getAllMagnets(undefined, 1, 1000);
      setMagnets(r.data || []);
    } catch { setMagnets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => { setPage(1); }, [search, filterType, filterStage]);

  const handleSave = async (data: any) => {
    if (editingMagnet) {
      await magnetService.updateMagnet(editingMagnet.id, data);
      addToast({ type: 'success', title: 'Magnet updated' });
    } else {
      await magnetService.createMagnet(data);
      addToast({ type: 'success', title: 'Magnet registered' });
    }
    setShowForm(false); setEditingMagnet(null);
    load();
  };

  const handleDelete = async (magnet: Magnet) => {
    if (!confirm(`Delete "${magnet.title}"?`)) return;
    await magnetService.deleteMagnet(magnet.id);
    addToast({ type: 'success', title: 'Magnet deleted' });
    load();
  };

  // Filter
  const filtered = magnets.filter(m => {
    if (search && !`${m.title} ${m.description} ${m.author}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType !== 'all' && m.contentType !== filterType) return false;
    if (filterStage && !m.journeySteps?.map(Number).includes(filterStage)) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / MAGNETS_PER_PAGE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * MAGNETS_PER_PAGE, safePage * MAGNETS_PER_PAGE);

  // Stats
  const byType = CONTENT_TYPES.map(ct => ({ ...ct, count: magnets.filter(m => m.contentType === ct.value).length }));

  if (showForm || editingMagnet) {
    return (
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => { setShowForm(false); setEditingMagnet(null); }}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors">←</button>
          <div>
            <h1 className="text-xl font-bold">{editingMagnet ? 'Edit Magnet' : 'Register New Magnet'}</h1>
            <p className="text-xs text-muted-foreground">Add a subsite page URL and configure tracking</p>
          </div>
        </div>
        <MagnetForm
          initial={editingMagnet || undefined}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingMagnet(null); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Magnet Library"
        description="Register subsite portal URLs and generate tracked engagement links for each ABM stage"
        actions={
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm">
            <Plus className="h-4 w-4" />Register Magnet
          </button>
        }
      />

      {/* Subsite domain config */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border">
        <Globe className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1">
          <p className="text-xs font-semibold text-foreground">Subsite portal domain</p>
          <p className="text-[10px] text-muted-foreground">All magnet pages live on this domain</p>
        </div>
        <input value={subsiteDomain} onChange={e => setSubsiteDomain(e.target.value)}
          className="w-64 px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs font-mono"
          placeholder="resources.cantier.com" />
        <a href={`https://${subsiteDomain}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-primary hover:underline">
          Open <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-2xl font-bold text-foreground">{magnets.length}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Registered magnets</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-2xl font-bold text-primary">{magnets.filter(m => m.subsiteUrl).length}</p>
          <p className="text-xs text-muted-foreground mt-0.5">With subsite URL</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{magnets.filter(m => m.contentType === 'assessment').length}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Assessment pages</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{magnets.reduce((s, m) => s + (m.deliveries || 0), 0)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total deliveries</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search magnets…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm">
          <option value="all">All types</option>
          {CONTENT_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
        </select>
        <select value={filterStage || ''} onChange={e => setFilterStage(e.target.value ? parseInt(e.target.value) : null)}
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm">
          <option value="">All stages</option>
          {JOURNEY_STAGES.map(s => <option key={s.id} value={s.id}>Stage {s.id}: {s.label}</option>)}
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <Globe className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No magnets registered yet</p>
          <p className="text-xs text-muted-foreground mt-1">Register subsite page URLs from your resources portal to start tracking engagement</p>
          <button onClick={() => setShowForm(true)}
            className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors mx-auto">
            <Plus className="h-4 w-4" />Register first magnet
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginated.map(m => (
              <MagnetCard key={m.id} magnet={m}
                onEdit={() => setEditingMagnet(m)}
                onDelete={() => handleDelete(m)}
                onTrack={() => setTrackingMagnet(m)}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              pageSize={MAGNETS_PER_PAGE}
              totalItems={filtered.length}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Tracked link modal */}
      {trackingMagnet && <TrackedLinkModal magnet={trackingMagnet} onClose={() => setTrackingMagnet(null)} />}
    </div>
  );
}
